// ── Supabase Client ───────────────────────────────────────────────────────────
// Production config is the default. An E2E harness may set
//   window.__ORDINEX_SUPABASE__ = { url, anonKey }
// BEFORE this script runs (Playwright addInitScript) to point the app at a
// disposable preview branch. In production that global is never set, so this
// falls through to the hardcoded prod values. Classic script — no ES exports
// (build.ps1 minifies this file without bundling; an `export` would leak into
// the .min.js and break the plain <script> load).
const _ORDINEX_PROD_SUPABASE = {
  url:     'https://rzbqqssscfilknnmralc.supabase.co',
  anonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJ6YnFxc3NzY2ZpbGtubm1yYWxjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODQyNDg4NTIsImV4cCI6MjA5OTgyNDg1Mn0.GH_lmftWio9WVnE1QH3OfI0jAdD06Tm9n2KL7oCGEck',
};
const _ordinexOverride = window.__ORDINEX_SUPABASE__;
const _ordinexSbCfg = (_ordinexOverride && _ordinexOverride.url && _ordinexOverride.anonKey)
  ? _ordinexOverride
  : _ORDINEX_PROD_SUPABASE;

const SUPABASE_URL      = _ordinexSbCfg.url;
const SUPABASE_ANON_KEY = _ordinexSbCfg.anonKey;

const sbClient = window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ═════════════════════════════════════════════════════════════════════════════
//  A DELETE THAT DELETED NOTHING MUST NOT LOOK LIKE SUCCESS
// ═════════════════════════════════════════════════════════════════════════════
// Row-level security does not refuse a DELETE — it filters the row out of the
// statement. Zero rows go, PostgREST answers 204, and supabase-js hands back
// { error: null }. Every caller in this app reads that as "deleted". So does the
// person at the screen: the list re-renders without the row and the record is
// still there, whole, feeding the home page, the chart, Job Costs and
// Production. That is the shape of the bug Renan hit on 2026-08-16 — a
// production report he deleted was still in the database nine minutes later,
// and every other screen was right to keep showing it.
//
// It happens whenever the session's claims no longer satisfy the policy: an
// expired or refreshed token, a tab left open across a sign-out, a role change.
// Nothing in the UI can tell, because there is no error to show.
//
// This makes every DELETE in the app — 44 call sites across admin, field and
// core, none of which asked for a row count — carry `Prefer: count=exact`, and
// checks the count on the way back. `data` is untouched (still null), so no
// caller sees a different shape; the only new behaviour is that a silent
// no-op now says so, once, in front of the person who thinks they deleted
// something.
//
// One place instead of 44: a call site that forgets the check cannot exist.
(function guardSilentDeletes() {
  // Columns that identify ONE record. Anything else (a parent id on its own,
  // a status, a date range) is a cleanup sweep, where zero rows is routine.
  const PRECISE_KEYS = new Set([
    'id', 'endpoint', 'token', 'topic_key', 'source_ref', 'source_id',
    'delivery_material_id', 'material_type_id', 'delivery_request_id',
  ]);

  const REFRESH_MSG =
    'Nothing was deleted.\n\n' +
    'The record is still there. This usually means your sign-in expired while ' +
    'this page was open.\n\n' +
    'Sign out and back in, then try again — and check the record before you ' +
    'assume it is gone.';

  // One dialog per burst, for real. The first cut released the latch INSIDE
  // the 60 ms timer, so any two deletes separated by a network round trip —
  // i.e. every sequential `await` loop in this app — each got their own modal,
  // which is the stacking the latch was written to prevent.
  let warning = false;
  let warnTimer = null;

  function report(table, precise, suppressed) {
    console.warn(`[ordinex] DELETE on ${table} removed 0 rows — RLS filtered it, ` +
                 'or the row was already gone.');
    // The dialog fires only for a PRECISE delete (filtered on id): there, zero
    // rows means the thing on the screen was not removed. Broad cleanup deletes
    // ("clear whatever entries this delivery had") hit zero rows legitimately
    // all the time and must stay silent.
    // A caller that inspects the count itself has already told the person
    // something accurate — "the office may have already scheduled it" — and a
    // second, generic "your sign-in expired" on top of it is noise that is
    // usually also wrong. Those sites ask for `return=representation`, so the
    // Prefer header is the signal.
    if (!precise || suppressed) return;
    if (warning) return;
    warning = true;
    clearTimeout(warnTimer);
    // Give the caller's own alert/re-render a tick to finish first, so this is
    // the LAST thing on screen rather than the first. The latch outlives the
    // dialog by a beat so a loop of failures cannot queue forty of them.
    warnTimer = setTimeout(() => {
      try { window.alert(REFRESH_MSG); } catch (_) { /* headless */ }
      setTimeout(() => { warning = false; }, 1500);
    }, 60);
  }

  const from = sbClient.from.bind(sbClient);
  sbClient.from = function (table) {
    const q = from(table);
    const del = q.delete.bind(q);
    q.delete = function (opts) {
      // count=exact returns the number of rows the statement actually touched
      // without returning the rows themselves — so `data` stays null and no
      // existing caller changes shape.
      const b = del({ ...(opts || {}), count: 'exact' });
      const then = b.then.bind(b);
      b.then = function (onOk, onErr) {
        return then((res) => {
          // count is null when PostgREST did not report one (older gateway,
          // or an RPC-shaped call); only a hard zero is evidence of a no-op.
          if (res && !res.error && res.count === 0) {
            // Filters are on the builder's URL by the time it executes. A
            // delete aimed at ONE row is one that filters on a unique key —
            // `id` is the common one, but plenty of single-object deletes in
            // this app key on source_ref, topic_key, endpoint or a pair of
            // ids, and those were silently exempt. A sweep is the shape with
            // no equality filter at all, or one that names a parent only.
            let precise = false, suppressed = false;
            try {
              const params = b.url && b.url.searchParams;
              precise = !!params && [...params.keys()].some(k => PRECISE_KEYS.has(k));
              // The caller asked for the deleted rows back: it can count them
              // itself, and several already do — do not talk over them.
              suppressed = String(b.headers?.Prefer || b.headers?.prefer || '')
                .includes('return=representation');
            } catch (_) { /* older client shape — stay console-only */ }
            report(table, precise, suppressed);
          }
          return onOk ? onOk(res) : res;
        }, onErr);
      };
      return b;
    };
    return q;
  };
})();

// A member removed from the company still signs in (the login itself is
// valid), but the token hook gives them no company: app_metadata.org_status
// is set and is not 'active'. True for that case only — an account with no
// company yet (mid-signup) has no org_status at all.
function sessionIsDeactivated(session) {
  try {
    const p = session.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const st = JSON.parse(atob(p)).app_metadata?.org_status;
    return st != null && st !== 'active';
  } catch { return false; }
}
